import { useState } from 'react';
import { Modal, Button } from '../core';
import { SearchableSelect, type SearchableSelectOption } from '../core/SearchableSelect';
import {
  EMPTY_REQUEST_FILTERS,
  type RequestFilters,
  type RequestStatusFilter,
  type RequestTypeFilter,
} from './requestFilters';

type RequestsFilterModalProps = {
  filters: RequestFilters;
  userOptions: SearchableSelectOption[];
  onApply: (filters: RequestFilters) => void;
  onClose: () => void;
};

const labelClass = 'block text-[12px] font-medium text-[#6b6b6b] mb-1';
const fieldClass =
  'w-full h-[42px] box-border px-3 border border-[#e0e0e0] rounded-lg text-[14px] text-[#1c1c1c] bg-white focus:outline-none focus:ring-2 focus:ring-[#0084c7] focus:border-transparent';

export const RequestsFilterModal = ({ filters, userOptions, onApply, onClose }: RequestsFilterModalProps) => {
  // edits stay local until "Anwenden"
  const [draft, setDraft] = useState<RequestFilters>(filters);
  const update = (patch: Partial<RequestFilters>) => setDraft((prev) => ({ ...prev, ...patch }));

  const rangeInvalid = !!draft.from && !!draft.to && draft.from > draft.to;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Anträge filtern"
      size="sm"
      footer={
        <div className="flex justify-between gap-3">
          <Button variant="ghost" onClick={() => onApply(EMPTY_REQUEST_FILTERS)}>
            Filter zurücksetzen
          </Button>
          <Button variant="primary" onClick={() => onApply(draft)} disabled={rangeInvalid}>
            Anwenden
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div>
          <span className={labelClass}>Zeitraum</span>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="date"
              aria-label="Von"
              value={draft.from}
              onChange={(e) => update({ from: e.target.value })}
              className={fieldClass}
            />
            <input
              type="date"
              aria-label="Bis"
              value={draft.to}
              onChange={(e) => update({ to: e.target.value })}
              className={fieldClass}
            />
          </div>
          {rangeInvalid && (
            <p className="text-[12px] text-red-600 mt-1">„Von" darf nicht nach „Bis" liegen.</p>
          )}
        </div>

        <div>
          <label htmlFor="requests-filter-type" className={labelClass}>Typ</label>
          <select
            id="requests-filter-type"
            value={draft.type}
            onChange={(e) => update({ type: e.target.value as RequestTypeFilter })}
            className={`form-select ${fieldClass}`}
          >
            <option value="all">Alle</option>
            <option value="holiday">Urlaub</option>
            <option value="overtime">Überstundenabbau</option>
          </select>
        </div>

        <div>
          <label htmlFor="requests-filter-status" className={labelClass}>Status (Frühere Anträge)</label>
          <select
            id="requests-filter-status"
            value={draft.status}
            onChange={(e) => update({ status: e.target.value as RequestStatusFilter })}
            className={`form-select ${fieldClass}`}
          >
            <option value="all">Alle</option>
            <option value="approved">Genehmigt</option>
            <option value="rejected">Abgelehnt</option>
          </select>
        </div>

        <div>
          <span className={labelClass}>Benutzer</span>
          <SearchableSelect
            value={draft.userId || undefined}
            onChange={(id) => update({ userId: id ?? '' })}
            options={userOptions}
            emptyOptionLabel="Alle Mitarbeiter"
            searchPlaceholder="Mitarbeiter suchen..."
            noResultsText="Keine Mitarbeiter gefunden"
            className="w-full h-[42px] text-[14px]"
          />
        </div>
      </div>
    </Modal>
  );
};
